import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPayments } from '../apiEndpoints';
import { currencySymbol } from '@/utils/currency';
import { IconChevronLeft as ChevronLeft, IconChevronRight as ChevronRight } from '@tabler/icons-react';
import { Select, SelectItem } from '@/components/ui/AppSelect';

export default function MyPaymentsTab() {
    const [page, setPage] = useState(1); // Start from page 1
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [payments, setPayments] = useState<any[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    // Fetch payments from backend with pagination
    const { data, isLoading, isError } = useQuery({
        queryKey: ['payments', page, rowsPerPage],
        queryFn: () => fetchPayments({ page, limit: rowsPerPage }),
    });
    // Handle backend response structure
    useEffect(() => {
        if (data?.data) {
            setPayments(data?.data?.data || []);
            setTotalRows(data?.data?.total || 0);
        }
    }, [data]);

    const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

    // Handle page changes
    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setPage(newPage);
        }
    };

    // Handle rows per page changes
    const handleRowsPerPageChange = (newRowsPerPage: number) => {
        setRowsPerPage(newRowsPerPage);
        setPage(1); // Reset to first page when changing rows per page
    };

    const pagerBtn = 'flex h-10 w-10 items-center justify-center rounded-full bg-grey99 text-grey10 ring-1 ring-black/[0.04] transition-colors hover:bg-grey95 disabled:opacity-40 dark:bg-white/[0.06] dark:text-white dark:ring-white/[0.06] dark:hover:bg-white/10';
    const pill = 'inline-flex h-7 items-center gap-1.5 rounded-full px-3 text-xs font-semibold';
    const stateRow = 'rounded-[20px] bg-grey99 px-4 py-10 text-center text-sm dark:bg-white/[0.04]';

    return (
        <div className="w-full">
            <div className="">
                <div className="mb-4 w-full rounded-[28px] bg-white p-3 ring-1 ring-black/[0.06] dark:bg-white/[0.04] dark:ring-white/10 sm:p-4">
                    <div className="mb-2 hidden grid-cols-[1.2fr_120px_1.4fr_140px] gap-4 px-5 py-2 md:grid">
                        <div className="text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted">Date & Time</div>
                        <div className="text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted">Price</div>
                        <div className="text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted">ID</div>
                        <div className="text-xs font-bold uppercase tracking-[0.14em] text-grey40 dark:text-nightMuted">Status</div>
                    </div>

                    <div className="flex flex-col gap-2">
                        {isLoading ? (
                            <div className={`${stateRow} text-grey40 dark:text-nightMuted`}>Loading payments...</div>
                        ) : isError ? (
                            <div className={`${stateRow} text-red-600 dark:text-red-400`}>Failed to load payments.</div>
                        ) : payments.length === 0 ? (
                            <div className={`${stateRow} text-grey40 dark:text-nightMuted`}>No payments found.</div>
                        ) : (
                            payments.map((payment: any, idx: number) => (
                                <div
                                    key={payment.paymentId || payment.id || payment._id || idx}
                                    className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2 rounded-[20px] bg-grey99 px-4 py-4 ring-1 ring-black/[0.03] dark:bg-white/[0.04] dark:ring-white/[0.06] md:grid-cols-[1.2fr_120px_1.4fr_140px] md:px-5"
                                >
                                    <div className="text-sm font-semibold text-grey10 dark:text-white">
                                        {payment.createdAt ? new Date(payment.createdAt).toLocaleDateString('en-US', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric'
                                        }) + ' - ' + new Date(payment.createdAt).toLocaleTimeString('en-US', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: false
                                        }) : payment.date || 'N/A'}
                                    </div>
                                    <div className="order-3 text-base font-bold tracking-[-0.02em] text-grey10 dark:text-white md:order-none md:text-sm">
                                        {payment.amount ? `${currencySymbol(payment.currency)}${Number(payment.amount).toFixed(2)}` : payment.price ? `${currencySymbol(payment.currency)}${Number(payment.price).toFixed(2)}` : 'N/A'}
                                    </div>
                                    <div className="order-4 col-span-2 min-w-0 break-all font-mono text-xs text-grey40 dark:text-nightMuted md:order-none md:col-span-1">
                                        {payment._id || payment.paymentId || payment.id || 'N/A'}
                                    </div>
                                    <div className="order-2 justify-self-end md:order-none md:justify-self-start">
                                        {payment.status === 'successful' || payment.status === 'completed' ? (
                                            <span className={`${pill} bg-[#009688]/10 text-[#009688] dark:bg-nightAccent/15 dark:text-nightAccent`}><span className="h-1.5 w-1.5 rounded-full bg-current" />Successful</span>
                                        ) : payment.status === 'unsuccessful' || payment.status === 'failed' || payment.status === 'pending' ? (
                                            <span className={`${pill} bg-[#E53935]/10 text-[#E53935] dark:bg-[#E53935]/15 dark:text-red-400`}><span className="h-1.5 w-1.5 rounded-full bg-current" />Unsuccessful</span>
                                        ) : (
                                            <span className={`${pill} bg-[#FF9800]/10 text-[#FF9800] dark:bg-[#FF9800]/15`}><span className="h-1.5 w-1.5 rounded-full bg-current" />Pending</span>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 px-1 pt-2">
                        <div className="flex items-center gap-2 text-sm text-grey40 dark:text-nightMuted">
                            <Select classNames={{ trigger: 'h-10 px-3 text-sm' }} aria-label="Select" selectedKeys={[String(rowsPerPage ?? '')]} onChange={e => handleRowsPerPageChange(Number(e.target.value))}>
                                <SelectItem key={String(10)}>10 Rows</SelectItem>
                                <SelectItem key={String(25)}>25 Rows</SelectItem>
                                <SelectItem key={String(50)}>50 Rows</SelectItem>
                            </Select>
                            <span className="ml-1">
                                {totalRows === 0 ? 0 : (page - 1) * rowsPerPage + 1} - {Math.min(page * rowsPerPage, totalRows)} of {totalRows}
                            </span>
                        </div>

                        <div className="flex items-center gap-1.5">
                            <button
                                className={pagerBtn}
                                disabled={page === 1}
                                onClick={() => handlePageChange(page - 1)}
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </button>

                            {Array.from({ length: Math.min(4, totalPages) }, (_, i) => {
                                const pageNum = i + Math.max(1, page - 1);
                                return pageNum <= totalPages ? (
                                    <button
                                        key={pageNum}
                                        className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold transition-colors ${page === pageNum
                                            ? 'bg-grey10 text-white dark:bg-white dark:text-nightBg'
                                            : 'text-grey20 hover:bg-grey99 dark:text-nightTextSecondary dark:hover:bg-white/10'
                                            }`}
                                        onClick={() => handlePageChange(pageNum)}
                                    >
                                        {pageNum}
                                    </button>
                                ) : null;
                            })}

                            <button
                                className={pagerBtn}
                                disabled={page === totalPages}
                                onClick={() => handlePageChange(page + 1)}
                            >
                                <ChevronRight className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="flex items-center gap-2 text-sm text-grey40 dark:text-nightMuted">
                            <span>Go to page</span>
                            <input
                                type="number"
                                min={1}
                                max={totalPages}
                                value={page}
                                onChange={e => handlePageChange(Number(e.target.value))}
                                className="h-10 w-16 rounded-full bg-grey99 px-2 text-center text-sm font-semibold text-grey10 ring-1 ring-black/[0.04] focus:outline-none focus:ring-2 focus:ring-primary/50 dark:bg-white/[0.06] dark:text-white dark:ring-white/10"
                            />
                            <button
                                className="inline-flex h-10 items-center justify-center rounded-full bg-grey10 px-4 text-sm font-semibold text-white transition-colors hover:bg-black dark:bg-white dark:text-nightBg dark:hover:bg-grey90"
                                onClick={() => handlePageChange(page)}
                            >
                                Go
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
